import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * Planet → zone drill-down for a Hide and Seek round's map: the planet map,
 * with each zone's own, more detailed map laid exactly over the part of the
 * planet it covers (zones share their planet's world coordinates), opened by
 * zooming to it and then fading it in; "back to planet" fades it out, then
 * zooms out. Used by the round map (Marco Polo circles) and the pathways map.
 *
 * `useZoneDrillDown` owns the state and gives GameMap its focus, layers,
 * regions and zoom limit; `ZoneDrillControls` is the zone picker and the
 * "Back to planet" button.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { boundsToMapPercentRect, } from '@swtorbit/shared-socket-events';
import { MAP_ZOOM_ANIMATION_MS } from './useMapZoom';
/** How long a zone's map takes to fade in or out (the stylesheet's .game-map__layer). */
const LAYER_FADE_MS = 250;
export function useZoneDrillDown(location, { enabled: wanted, startZoneId, resolveImageUrl }) {
    const world = location.world;
    const enabled = wanted && !!world;
    const allZones = useMemo(() => location.zones ?? [], [location.zones]);
    const start = enabled ? startZoneId : null;
    const [focusZoneId, setFocusZoneId] = useState(start);
    const [shownZoneId, setShownZoneId] = useState(start);
    const [spotFocus, setSpotFocus] = useState(null);
    const timer = useRef(null);
    useEffect(() => () => {
        if (timer.current)
            clearTimeout(timer.current);
    }, []);
    const later = (ms, fn) => {
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = setTimeout(fn, ms);
    };
    const zoneRects = useMemo(() => {
        const rects = new Map();
        if (!enabled || !world)
            return rects;
        for (const zone of allZones) {
            const rect = boundsToMapPercentRect(world.bounds, zone.bounds);
            if (rect)
                rects.set(zone.id, rect);
        }
        return rects;
    }, [enabled, world, allZones]);
    const zones = useMemo(() => allZones.filter((zone) => zoneRects.has(zone.id)), [allZones, zoneRects]);
    const maxZoom = useMemo(() => {
        let fit = 8;
        for (const rect of zoneRects.values())
            fit = Math.max(fit, 200 / Math.max(rect.w, rect.h, 0.01));
        return Math.min(40, fit);
    }, [zoneRects]);
    function openZone(zoneId) {
        setSpotFocus(null);
        setShownZoneId(null);
        setFocusZoneId(zoneId);
        later(MAP_ZOOM_ANIMATION_MS, () => setShownZoneId(zoneId));
    }
    function backToPlanet() {
        setSpotFocus(null);
        if (shownZoneId === null) {
            setFocusZoneId(null);
            return;
        }
        setShownZoneId(null);
        later(LAYER_FADE_MS, () => setFocusZoneId(null));
    }
    function zoneAt(x, y) {
        let best = null;
        for (const zone of zones) {
            const { minX, minY, maxX, maxY } = zone.bounds;
            if (x < minX || x > maxX || y < minY || y > maxY)
                continue;
            const area = (maxX - minX) * (maxY - minY);
            const bestArea = best ? (best.bounds.maxX - best.bounds.minX) * (best.bounds.maxY - best.bounds.minY) : Infinity;
            if (area < bestArea)
                best = zone;
        }
        return best?.id ?? null;
    }
    const focus = spotFocus ?? (focusZoneId !== null ? zoneRects.get(focusZoneId) ?? null : null);
    const layers = zones
        .filter((zone) => zone.id === focusZoneId || zone.id === shownZoneId)
        .map((zone) => ({
        id: `zone:${zone.id}`,
        imageUrl: resolveImageUrl(zone.image.url),
        rect: zoneRects.get(zone.id),
        name: zone.name,
        visible: zone.id === shownZoneId,
    }));
    const regions = zones.map((zone) => ({ id: String(zone.id), rect: zoneRects.get(zone.id), label: zone.name }));
    return {
        enabled,
        zones,
        zoneRects,
        maxZoom,
        focusZoneId,
        focus,
        layers,
        regions,
        zoomedIn: focusZoneId !== null || spotFocus !== null,
        openZone,
        backToPlanet,
        focusOn: setSpotFocus,
        zoneAt,
    };
}
/** The zone picker and "Back to planet", for a drill-down map. */
export function ZoneDrillControls({ drill, location }) {
    if (!drill.enabled)
        return null;
    return (_jsxs("div", { className: "d-flex flex-wrap align-items-center gap-2xs", children: [drill.zones.length > 0 && (_jsxs("select", { className: "select select--sm", "aria-label": "Map", value: drill.focusZoneId ?? '', onChange: (event) => (event.target.value ? drill.openZone(Number(event.target.value)) : drill.backToPlanet()), children: [_jsxs("option", { value: "", children: [location.world?.name ?? location.planetName ?? 'Planet', " (planet)"] }), drill.zones.map((zone) => (_jsx("option", { value: zone.id, children: zone.name ?? 'Zone' }, zone.id)))] })), drill.zoomedIn && (_jsx("button", { type: "button", className: "btn btn--sm btn--outline btn--slate", onClick: drill.backToPlanet, children: "Back to planet" }))] }));
}
